import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { createCampaign, deleteCampaign, estimateCampaignAudience, getCampaigns, runCampaignAction, updateCampaign } from "api/bMarketingApi";
import { getCampaignsRefetchInterval } from "./campaignPolling";

export function useCampaigns(organisationId, options = {}) {
  const page = options.page || 1;
  const pageSize = options.pageSize || 25;
  const campaignType = options.campaignType || "";
  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ["campaigns", organisationId, page, pageSize, campaignType],
    queryFn: () => getCampaigns(organisationId, { page, pageSize, campaignType }).then((res) => res.data),
    enabled: !!organisationId && options.enabled !== false,
    placeholderData: (previousData) => previousData,
    refetchInterval: (query) => getCampaignsRefetchInterval(query.state.data?.results),
  });

  return {
    campaigns: data?.results || [],
    pagination: {
      count: data?.count || 0,
      numPages: data?.num_pages || 1,
      currentPage: data?.current_page || 1,
      hasNext: data?.has_next || false,
      hasPrevious: data?.has_previous || false,
    },
    isLoading,
    error,
    refetch,
  };
}

export function useCreateCampaign() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data) => createCampaign(data).then((res) => res.data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["campaigns"] });
      queryClient.invalidateQueries({ queryKey: ["emailActivityLogs"] });
      queryClient.invalidateQueries({ queryKey: ["emailActivityStats"] });
    },
  });
}

export function useUpdateCampaign() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ campaignId, data }) => updateCampaign(campaignId, data).then((res) => res.data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["campaigns"] });
      queryClient.invalidateQueries({ queryKey: ["emailActivityLogs"] });
      queryClient.invalidateQueries({ queryKey: ["emailActivityStats"] });
    },
  });
}

export function useCampaignAction() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ campaignId, action }) => runCampaignAction(campaignId, action).then((res) => res.data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["campaigns"] });
      queryClient.invalidateQueries({ queryKey: ["emailActivityLogs"] });
      queryClient.invalidateQueries({ queryKey: ["emailActivityStats"] });
    },
  });
}

export function useCampaignAudienceEstimate(
  organisationId,
  audienceType,
  segments = [],
  campaignType = "email",
  tags = [],
  options = {},
) {
  const includeRisky = Boolean(options.includeRisky);
  const includeUnknown = Boolean(options.includeUnknown);
  const normalizedSegments = [...segments].map(Number).sort((left, right) => left - right);
  const normalizedTags = [...tags].map(Number).sort((left, right) => left - right);

  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ["campaignAudienceEstimate", organisationId, campaignType, audienceType, normalizedSegments, normalizedTags, includeRisky, includeUnknown],
    queryFn: () => estimateCampaignAudience({
      organisation: organisationId,
      campaign_type: campaignType,
      audience_type: audienceType,
      segments: normalizedSegments,
      tags: normalizedTags,
      include_risky_emails: includeRisky,
      include_unknown_emails: includeUnknown,
    }).then((res) => res.data),
    enabled: !!organisationId && options.enabled !== false && (
      audienceType === "all_contacts" ||
      (audienceType === "segment" && normalizedSegments.length > 0) ||
      (audienceType === "tag" && normalizedTags.length > 0)
    ),
  });

  return {
    recipientCount: data?.recipient_count || 0,
    selectedCount: data?.eligibility?.selected_count || 0,
    excludedCount: data?.eligibility?.excluded_count || 0,
    exclusions: data?.eligibility?.exclusions || {},
    validationCounts: data?.eligibility?.validation_counts || {},
    segmentCount: data?.segment_count || 0,
    tagCount: data?.tag_count || 0,
    isLoading,
    error,
    refetch,
  };
}

export function useDeleteCampaign() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (campaignId) => deleteCampaign(campaignId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["campaigns"] });
      queryClient.invalidateQueries({ queryKey: ["emailActivityLogs"] });
      queryClient.invalidateQueries({ queryKey: ["emailActivityStats"] });
    },
  });
}
